import {useAuth} from '@apps-common/auth-react';
import {Box, Divider, Slide} from '@mui/material';
import PropTypes from 'prop-types';
import React, {useMemo} from 'react';
import {connect} from 'react-redux';

import AccessFilterToggle from './access-filter-toggle.jsx';
import Breadcrumbs from './breadcrumbs.jsx';
import Footer from './footer.jsx';
import ResultsListV2 from './results-list-v2/index.jsx';
import ScenesListV2 from './scenes-list-v2/index.jsx';
import {
  setResultsSortDirectionAndPageNumber,
  setScenesSortDirection,
} from '@explorer/actions/sidebar';
import {showModal} from '@explorer/actions/ui';
import {trackEvent} from '@explorer/analytics';
import {useGetPlanSubscriptionsQuery} from '@explorer/api/apiSlice.js';
import DeselectMenuButton from '@explorer/components/sidebars/search/results-panel/deselect-menu-button';
import SortButton from '@explorer/components/sidebars/sort-button';
import {LoadingMessage} from '@explorer/components/ui/message-container';
import {MODALS} from '@explorer/constants';
import {selectFilteredResultsList} from '@explorer/selectors/results';
import {isTrialUser, userCanDownload} from '@explorer/util/user.js';

const DailyResultsPanel = ({activeResult, ...props}) => {
  const auth = useAuth();
  const resultsCount = props.results.length;
  const {data: subscriptions} = useGetPlanSubscriptionsQuery();
  const canDownload = useMemo(
    () => userCanDownload(auth.user?.claims, subscriptions),
    [auth.user?.claims, subscriptions]
  );

  const onOrderClick = () => {
    trackEvent('Orders Dialog - Launch', {
      itemCount: props.orderableSelectedItems.length,
    });
    props.showModal(MODALS.ORDER);
  };

  if (props.resultsLoading && resultsCount === 0) {
    return <LoadingMessage suffix="results" />;
  }

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1,
          flexShrink: 0,
        }}
      >
        {!isTrialUser(auth.user.claims) && !activeResult && (
          <AccessFilterToggle />
        )}
        <Breadcrumbs count={resultsCount} />
        {!activeResult && (
          <Box
            sx={{
              marginRight: 0.5,
            }}
          >
            <DeselectMenuButton />
          </Box>
        )}
        <SortButton
          sortDirection={
            activeResult
              ? props.scenesSortDirection
              : props.resultsSortDirection
          }
          setDirection={
            activeResult
              ? props.setScenesSortDirection
              : props.setResultsSortDirectionAndPageNumber
          }
        />
      </Box>
      <Divider />
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Slide in={!activeResult} direction="left">
          <Box
            sx={{
              height: !activeResult ? '100%' : 0,
            }}
          >
            <ResultsListV2 canDownload={canDownload} />
          </Box>
        </Slide>
        <Slide in={Boolean(activeResult)} direction="right">
          <Box
            sx={{
              height: activeResult ? '100%' : 0,
              overflow: 'auto',
            }}
          >
            {activeResult && (
              <ScenesListV2
                resultId={activeResult?.id}
                canDownload={canDownload}
              />
            )}
          </Box>
        </Slide>
      </Box>
      <Footer
        count={props.orderableSelectedItems.length}
        openApiDialog={() => props.showModal(MODALS.API)}
        onOrderClick={() => onOrderClick()}
      />
    </>
  );
};

DailyResultsPanel.propTypes = {
  activeResult: PropTypes.object,
  orderableSelectedItems: PropTypes.array,
  results: PropTypes.array.isRequired,
  resultsLoading: PropTypes.bool,
  showFiltersPanel: PropTypes.func,
  showModal: PropTypes.func,
  searchName: PropTypes.string,
  resultsSortDirection: PropTypes.string,
  scenesSortDirection: PropTypes.string,
  setResultsSortDirectionAndPageNumber: PropTypes.func,
  setScenesSortDirection: PropTypes.func,
};

const mapStateToProps = state => ({
  activeResult: state.results.resultsById[state.sidebar.activeResultId],
  orderableSelectedItems: state.results.orderItems,
  results: selectFilteredResultsList(state),
  resultsLoading: state.results.loading,
  searchName: state.search.name,
  resultsSortDirection: state.sidebar.resultsSortDirection,
  scenesSortDirection: state.sidebar.scenesSortDirection,
});

const mapDispatchProps = {
  showModal,
  setResultsSortDirectionAndPageNumber,
  setScenesSortDirection,
};

export default connect(mapStateToProps, mapDispatchProps)(DailyResultsPanel);
